import React from 'react';
import AppointmentTypeSelector from './AppointmentTypeSelector';
import AddressInputStep from './AddressInputStep';
import DateTimeSelectionStep from './DateTimeSelectionStep';
import CustomTimeRequestStep from './CustomTimeRequestStep';
import FixedLocationSelector from './FixedLocationSelector';
import { getBookingPageTheme } from '../../utility/themeUtils';
import { BookingFlowContext } from '../../contexts/BookingFlowContext';
import { useContext } from 'react';
import useAppointmentSetup from '../../hooks/useAppointmentSetup';
import { deriveCartDeliveryModes } from '../../utility/bookingFlowUtils';

const AppointmentSetupStep = (props) => {
  // useContext returns null when outside BookingFlowProvider (reschedule mode)
  const contextValue = useContext(BookingFlowContext);

  // Merge: context values take priority in normal booking mode,
  // props take priority in reschedule mode (context is null)
  const serviceLocationTypes = contextValue?.serviceLocationTypes ?? props.serviceLocationTypes;
  const selectedAppointmentType = contextValue?.selectedAppointmentType ?? props.selectedAppointmentType;
  const onAppointmentTypeChange = contextValue?.handleAppointmentTypeChange ?? props.onAppointmentTypeChange;
  const selectedAddress = contextValue?.selectedAddress ?? props.selectedAddress;
  const onAddressChange = contextValue?.handleAddressChange ?? props.onAddressChange;
  const selectedLocation = contextValue?.selectedLocation ?? props.selectedLocation;
  const onLocationChange = contextValue?.setSelectedLocation ?? props.onLocationChange;
  const selectedDate = contextValue?.selectedDate ?? props.selectedDate;
  const onDateChange = contextValue?.setSelectedDate ?? props.onDateChange;
  const selectedTime = contextValue?.selectedTime ?? props.selectedTime;
  const onTimeChange = contextValue?.setSelectedTime ?? props.onTimeChange;
  const primaryColor = contextValue
    ? getBookingPageTheme(contextValue.bookingPage).primaryColor
    : (props.primaryColor || 'blue-600');
  const themeMode = contextValue
    ? getBookingPageTheme(contextValue.bookingPage).themeMode
    : (props.themeMode || 'light');
  const businessName = contextValue?.bookingPage?.businessName ?? props.businessName;
  const bookingPage = contextValue?.bookingPage ?? props.bookingPage;
  const slug = contextValue?.slug ?? props.slug;
  const travelFee = contextValue?.travelFee ?? props.travelFee;
  const onTravelFeeChange = contextValue?.setTravelFee ?? props.onTravelFeeChange;
  const currency = contextValue?.currency ?? props.currency;
  const fixedLocationData = contextValue?.fixedLocationData ?? props.fixedLocationData;
  const onOutsideAvailabilityChange = contextValue?.handleOutsideAvailabilityChange ?? props.onOutsideAvailabilityChange;
  const onCustomTimeRequestChange = contextValue?.handleCustomTimeRequestChange ?? props.onCustomTimeRequestChange;
  const selectedServices = contextValue?.selectedServices ?? props.selectedServices;
  const totalServiceDuration = contextValue?.totalServiceDuration ?? props.totalServiceDuration;
  const searchBias = contextValue?.searchBias ?? props.searchBias;

  // Reschedule-only props — never from context
  const isRescheduleMode = props.isRescheduleMode || false;
  const prePopulatedAvailabilityIds = props.prePopulatedAvailabilityIds || [];
  const rescheduleLocationInfo = props.rescheduleLocationInfo || null;
  const appointment = props.appointment || null;
  const isFixedRescheduleMode = props.isFixedRescheduleMode || false;
  const locationMismatchError = props.locationMismatchError || false;

  // Derive hasFixed/hasMobile from the selected services' delivery types,
  // bounded by the provider-wide serviceLocationTypes. Falls back to the
  // provider-wide values when no services are selected (back-compat).
  // useAppointmentSetup's prop shape is unchanged — only the source changes.
  const {
    hasFixed,
    hasMobile,
  } = deriveCartDeliveryModes(selectedServices, serviceLocationTypes ?? { hasFixed: false, hasMobile: false });

  // --- All orchestration logic lives in the hook ---
  const {
    addressSectionRef,
    locationSectionRef,
    dateTimeSectionRef,
    hasMultipleOptions,
    showDateTimeSelection,
    showCustomTimeRequest,
    canProceedToDateTime,
    customTimeRequested,
    availabilityIds,
    timeZoneId,
    handleAppointmentTypeSelect,
    handleLocationSelect,
    handleAddressSelect,
    handleValidationChange,
    handleRequestCustomTime,
    handleBackToTimeSlots,
  } = useAppointmentSetup({
    selectedAppointmentType,
    onAppointmentTypeChange,
    selectedAddress,
    onAddressChange,
    selectedLocation,
    onLocationChange,
    fixedLocationData,
    bookingPage,
    onOutsideAvailabilityChange,
    onCustomTimeRequestChange,
    isRescheduleMode,
    prePopulatedAvailabilityIds,
    rescheduleLocationInfo,
    hasFixed,
    hasMobile,
  });

  return (
    <div className="space-y-8">
      {/* Show location info in reschedule mode */}
      {isRescheduleMode && !locationMismatchError && (rescheduleLocationInfo || appointment?.serviceAddress) && (
        <div className="bg-themed-card border-themed rounded-lg border p-4">
          <div className="flex items-start space-x-3">
            <div className="flex-shrink-0 mt-1">
              <svg className="w-5 h-5 text-themed-accent" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
              </svg>
            </div>
            <div className="flex-1 min-w-0">
              <h3 className="text-sm font-medium text-themed-primary mb-1">
                {selectedAppointmentType === 'mobile' ? 'Service Location' : 'Appointment Location'}
              </h3>
              <p className="text-sm text-themed-secondary">
                {selectedAppointmentType === 'mobile' && appointment?.serviceAddress?.full
                  ? appointment.serviceAddress.full
                  : rescheduleLocationInfo?.address}
              </p>
            </div>
          </div>
        </div>
      )}

      {/* Only show appointment type selector if there are multiple options and not in reschedule mode */}
      {!isRescheduleMode && hasMultipleOptions && (
        <AppointmentTypeSelector
          serviceLocationTypes={serviceLocationTypes}
          selectedType={selectedAppointmentType}
          onTypeChange={handleAppointmentTypeSelect}
          primaryColor={primaryColor}
          themeMode={themeMode}
          businessName={businessName}
        />
      )}

      {/* Show location selection when fixed is selected and multiple locations available and not in fixed reschedule mode */}
      {selectedAppointmentType === 'fixed' && fixedLocationData?.locations?.length >= 1 && !isFixedRescheduleMode && (
        <div ref={locationSectionRef} className="scroll-mt-6 animate-in fade-in slide-in-from-top-4 duration-500">
          <FixedLocationSelector
            fixedLocationData={fixedLocationData}
            selectedLocation={selectedLocation}
            onLocationChange={handleLocationSelect}
            primaryColor={primaryColor}
            themeMode={themeMode}
            businessName={businessName}
          />
        </div>
      )}

      {/* Show address input when mobile is selected and not in reschedule mode */}
      {!isRescheduleMode && selectedAppointmentType === 'mobile' && (
        <div ref={addressSectionRef} className="scroll-mt-6 animate-in fade-in slide-in-from-top-4 duration-500">
          <AddressInputStep
            selectedAddress={selectedAddress}
            onAddressChange={handleAddressSelect}
            primaryColor={primaryColor}
            themeMode={themeMode}
            businessName={businessName}
            bookingPage={bookingPage}
            slug={slug}
            travelFee={travelFee}
            onTravelFeeChange={onTravelFeeChange}
            currency={currency}
            onValidationChange={handleValidationChange}
            searchBias={searchBias}
          />
        </div>
      )}

      {/* Show date/time selection when ready */}
      {showDateTimeSelection && !locationMismatchError && (
        <div ref={dateTimeSectionRef} className="scroll-mt-6 animate-in fade-in slide-in-from-top-4 duration-500">
          <DateTimeSelectionStep
            serviceLocationTypes={serviceLocationTypes}
            selectedAppointmentType={selectedAppointmentType}
            onAppointmentTypeChange={onAppointmentTypeChange}
            selectedDate={selectedDate}
            onDateChange={onDateChange}
            selectedTime={selectedTime}
            onTimeChange={onTimeChange}
            primaryColor={primaryColor}
            themeMode={themeMode}
            businessName={businessName}
            hideAppointmentTypeSelector={true}
            slug={slug}
            availabilityIds={availabilityIds}
            timeZoneId={timeZoneId}
            bookingPage={bookingPage}
            onRequestCustomTime={handleRequestCustomTime}
            selectedServices={selectedServices}
            totalServiceDuration={totalServiceDuration}
          />
        </div>
      )}

      {/* Show custom time request for outside availability or calendar-initiated requests */}
      {showCustomTimeRequest && !locationMismatchError && (
        <div ref={dateTimeSectionRef} className="scroll-mt-6 animate-in fade-in slide-in-from-top-4 duration-500">
          <CustomTimeRequestStep
            selectedDate={selectedDate}
            onDateChange={onDateChange}
            selectedTime={selectedTime}
            onTimeChange={onTimeChange}
            primaryColor={primaryColor}
            themeMode={themeMode}
            businessName={businessName}
            onBackToTimeSlots={customTimeRequested && canProceedToDateTime ? handleBackToTimeSlots : null}
            showBackButton={customTimeRequested && canProceedToDateTime}
          />
        </div>
      )}
    </div>
  );
};

export default AppointmentSetupStep;
